/**
 * jornadaTicketEscPos.ts — Bytes ESC/POS y preview térmico del resumen global de jornada.
 */

import type { JornadaTicketModel } from "./buildJornadaTicketModel";
import { buildJornadaTicketPlainText } from "./buildJornadaTicketPlainText";
import {
  encodePlainTextToEscPos,
  plainTextLinesToPreviewLines,
} from "./escPosPlainTextDecode";
import {
  getTicketMaxWidth,
  sanitizeForThermal,
  type ThermalPreviewLine,
} from "./escposLayout";

export interface JornadaEscPosInput {
  model: JornadaTicketModel;
  paperWidthMm: 58 | 80;
  includeTurnos?: boolean;
}

const UNICODE_TO_LATIN1: Record<string, number> = {
  "\u00c1": 0xc1,
  "\u00c9": 0xc9,
  "\u00cd": 0xcd,
  "\u00d3": 0xd3,
  "\u00da": 0xda,
  "\u00dc": 0xdc,
  "\u00d1": 0xd1,
  "\u00e1": 0xe1,
  "\u00e9": 0xe9,
  "\u00ed": 0xed,
  "\u00f3": 0xf3,
  "\u00fa": 0xfa,
  "\u00fc": 0xfc,
  "\u00f1": 0xf1,
  "\u00a1": 0xa1,
  "\u00bf": 0xbf,
  "\u00b0": 0xb0,
};

function encodeThermalLineBytes(s: string): Uint8Array {
  const cleaned = sanitizeForThermal(s.replace(/\u20ac/g, "EUR")).normalize(
    "NFC",
  );
  const bytes: number[] = [];
  for (const ch of cleaned) {
    const code = ch.codePointAt(0) ?? 0;
    if (code <= 0xff) {
      bytes.push(code);
      continue;
    }
    const mapped = UNICODE_TO_LATIN1[ch];
    bytes.push(mapped !== undefined ? mapped : 0x3f);
  }
  return new Uint8Array(bytes);
}

/** Cada línea termina en LF — requerido para preview e impresión térmica. */
function lineLatin1(s: string): Uint8Array {
  const body = encodeThermalLineBytes(s);
  const out = new Uint8Array(body.length + 1);
  out.set(body);
  out[out.length - 1] = 0x0a;
  return out;
}

function buildPlainTextLines(input: JornadaEscPosInput): string[] {
  return buildJornadaTicketPlainText({
    model: input.model,
    paperWidthMm: input.paperWidthMm,
    includeTurnos: input.includeTurnos,
  });
}

export function buildJornadaEscPos(input: JornadaEscPosInput): Uint8Array {
  const cols = getTicketMaxWidth(input.paperWidthMm);
  const lines = buildPlainTextLines(input);
  return encodePlainTextToEscPos(lines, cols, lineLatin1, { includeCut: true });
}

/** Preview térmico — misma tubería que el cierre (texto plano + layout). */
export function buildJornadaPreviewLines(
  input: JornadaEscPosInput,
): ThermalPreviewLine[] {
  const cols = getTicketMaxWidth(input.paperWidthMm);
  const lines = buildPlainTextLines(input);
  return plainTextLinesToPreviewLines(lines, cols);
}

export function buildJornadaPlainTextLines(
  input: JornadaEscPosInput,
): string[] {
  return buildPlainTextLines(input);
}
